'use client'

/**
 * v3 Marketplace — Bill prototype 2026-05-30 (app.go-atrium.com).
 *
 * Top → bottom:
 *   1. Eyebrow                  → Marketplace + meta on the right
 *   2. Centered hero            — h1 + sub + search bar (search drives
 *                                  Connectors + Commands filtering below)
 *   3. AxisPills                — All / Channels / Subchannels / Commands /
 *                                  Your prompts
 *   4. Featured Rail            — 3 FeatureCards + scroll-more placeholder
 *   5. ALL CHANNELS grid        — full-bleed gradient cards → /channels/[key]
 *   6. CONNECTORS accordion     — collapsible per-channel groups; expanding
 *                                  reveals a grid of connector cards
 *   7. COMMANDS rail            — horizontal scroller of all commands + a
 *                                  Create automation placeholder card at end
 *   8. PromptsPanel             — saved prompts / login CTA / empty state
 *
 * Search bar's `searchTerm` state filters the Connectors accordion (channel
 * groups with zero matches are hidden, matching groups auto-expand) and the
 * Commands rail (name + short_description match).
 */

import { useMemo, useState, type ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { HugeiconsIcon } from '@hugeicons/react'
import { SearchIcon, PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded'
import { SettingsConnectionPanel } from '@/components/settings/SettingsConnectionPanel'
import { SectionHeading, SectionLabel } from '@/components/shared/SectionLabel'
import { GateProvider, useGate } from './gate/GateProvider'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { AxisPills, type AxisId } from './AxisPills'
import { Rail } from './Rail'
import { FeatureCard } from './FeatureCard'
import { ChannelGridCard } from './ChannelGridCard'
import { ChannelOverlay } from './ChannelOverlay'
import { CommandCard } from './CommandCard'
import { PlaceholderCard } from './PlaceholderCard'
import { PromptsPanel } from './PromptsPanel'
import { PromoBanner } from './PromoBanner'
import { ConnectorsAccordion } from './ConnectorsAccordion'
import { features, searchPlaceholder } from './marketplace-data'
import type {
  CatalogChannelView,
  CatalogSubchannelView,
} from '@/lib/connectors/get-catalog'
import type {
  MarketplaceCommand,
  MarketplacePrompt,
  MarketplacePromo,
} from '@/lib/marketplace/queries'

export function MarketplacePage({
  isLoggedIn,
  promo,
  channels,
  commands,
  prompts,
  initialChannelKey,
}: {
  isLoggedIn: boolean
  promo: MarketplacePromo | null
  channels: CatalogChannelView[]
  commands: MarketplaceCommand[]
  prompts: MarketplacePrompt[]
  initialChannelKey?: string
}) {
  const router = useRouter()
  const [searchTerm, setSearchTerm] = useState('')
  // Which axis pill is selected. Selecting one sorts its section to the top
  // (directly below the pill row); 'all' keeps the default order.
  const [axis, setAxis] = useState<AxisId>(initialChannelKey ? 'subchannels' : 'all')
  const [openSubchannel, setOpenSubchannel] = useState<CatalogSubchannelView | null>(null)
  const [selectedChannelKey, setSelectedChannelKey] = useState<string | null>(null)

  const subchannelCount = channels.reduce((sum, c) => sum + c.subchannels.length, 0)

  const filteredCommands = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return commands
    return commands.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        (c.short_description ?? '').toLowerCase().includes(term),
    )
  }, [commands, searchTerm])

  // Search → results below the bar. When the search bar has a term we surface
  // matches (channels / connectors / commands) directly under the search +
  // pills — mirroring the way an axis pill brings its section to the top —
  // instead of only filtering the sections in place.
  const query = searchTerm.trim().toLowerCase()
  const searchActive = query.length > 0
  const matchedChannels = useMemo(
    () =>
      !searchActive
        ? []
        : channels.filter(
            (c) =>
              c.name.toLowerCase().includes(query) ||
              (c.key ?? '').toLowerCase().includes(query) ||
              (c.description ?? '').toLowerCase().includes(query),
          ),
    [channels, query, searchActive],
  )
  const connectorMatchCount = useMemo(
    () =>
      !searchActive
        ? 0
        : channels.reduce(
            (n, c) =>
              n +
              c.subchannels.filter(
                (s) =>
                  s.name.toLowerCase().includes(query) ||
                  (s.description ?? '').toLowerCase().includes(query),
              ).length,
            0,
          ),
    [channels, query, searchActive],
  )
  const resultCount = matchedChannels.length + connectorMatchCount + filteredCommands.length
  const selectedChannel = useMemo(
    () => channels.find((channel) => channel.key === selectedChannelKey) ?? null,
    [channels, selectedChannelKey],
  )
  const enablingChannel = useMemo(
    () => channels.find((channel) => channel.key === openSubchannel?.channel_key) ?? null,
    [channels, openSubchannel],
  )

  const handleOpenSubchannel = (sub: CatalogSubchannelView) => {
    setSelectedChannelKey(null)
    setOpenSubchannel(sub)
  }

  // Orderable sections, keyed by id. Selecting an axis pill moves its matching
  // section to the front so it renders directly below the pill row.
  const SECTION_IDS = ['featured', 'channels', 'connectors', 'commands', 'prompts'] as const
  type SectionId = (typeof SECTION_IDS)[number]

  const axisToSection: Record<AxisId, SectionId | null> = {
    all: null,
    channels: 'channels',
    subchannels: 'connectors',
    commands: 'commands',
    'your-prompts': 'prompts',
  }

  const target = axisToSection[axis]
  const orderedSectionIds: SectionId[] = target
    ? [target, ...SECTION_IDS.filter((id) => id !== target)]
    : [...SECTION_IDS]

  const sectionNodes: Record<SectionId, ReactNode> = {
    featured: (
      <Rail eyebrow="Featured this week" title="Premium streaming, ready to launch">
        {features.map((f) => (
          <FeatureCard
            key={f.id}
            feature={f}
            onOpenChannel={setSelectedChannelKey}
          />
        ))}
        <PlaceholderCard size="hero" title="3 more" subtitle="scroll →" icon="arrow" />
      </Rail>
    ),
    channels: (
      <section className="mt-8">
        <SectionHeading label="All channels" title="Build your stack" />
        <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3.5">
          {channels.map((c) => (
            <ChannelGridCard
              key={c.key}
              channel={c}
              onOpen={setSelectedChannelKey}
            />
          ))}
        </div>
      </section>
    ),
    connectors: (
      <section className="mt-8">
        <SectionHeading
          label="Connectors"
          title="Every platform Atrium speaks to"
        />
        <ConnectorsAccordion
          channels={channels}
          searchTerm={searchTerm}
          initialOpenChannelKey={initialChannelKey}
          isLoggedIn={isLoggedIn}
          onOpen={handleOpenSubchannel}
        />
      </section>
    ),
    commands: (
      <Rail tight eyebrow="Commands" title="Automations, ready to install">
        {filteredCommands.map((c) => (
          <CommandCard key={c.id} command={c} />
        ))}
        <CreateAutomationCard />
      </Rail>
    ),
    prompts: <PromptsPanel prompts={prompts} />,
  }

  return (
    <GateProvider isLoggedIn={isLoggedIn}>
      {/* --mp-pad-* are zeroed so the Featured/Commands rails and PromptsPanel
          align flush with the "All channels" grid (which sits at the container's
          px-[26px] edge). They were 2.75rem/1.5rem, which pushed the rails ~44px
          further right than the grid headings — visibly misaligned. */}
      <div className="min-h-full bg-canvas text-text-strong [--mp-pad-l:0px] [--mp-pad-r:0px]">
        <LobbyHeaderMeta>
          <span>
            {channels.length} channel{channels.length === 1 ? '' : 's'} ·{' '}
            {subchannelCount} connector{subchannelCount === 1 ? '' : 's'} ·{' '}
            {commands.length} command{commands.length === 1 ? '' : 's'}
          </span>
        </LobbyHeaderMeta>

        {/* Centered hero + search bar */}
        <div className="py-[48px] pb-[32px] text-center">
          <h1 className="m-0 text-[54px] font-bold leading-[1.02] tracking-[-0.035em]">
            Every ad channel.
            <br />
            <span className="italic text-brand">One conversation.</span>
          </h1>
          <p className="mx-auto mt-[22px] max-w-[580px] text-[15px] leading-[1.5] text-text">
            Google. Meta. Streaming. Programmatic. Connect once, operate everything
            from a single conversation.
          </p>
          <label className="mx-auto mt-[22px] flex max-w-[560px] items-center gap-2.5 rounded-card border-[0.5px] border-line bg-surface px-4 py-[13px] shadow-v3-search">
            <HugeiconsIcon
              icon={SearchIcon}
              className="size-5 text-text-muted"
              aria-hidden
            />
            <input
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-text-strong outline-0 placeholder:text-text-muted"
              type="search"
              placeholder={searchPlaceholder}
              aria-label="Search marketplace"
              autoComplete="off"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
        </div>

        <AxisPills
          active={axis}
          onSelect={setAxis}
          counts={{
            channels: channels.length,
            subchannels: subchannelCount,
            commands: commands.length,
            prompts: prompts.length,
          }}
        />

        <div className="mx-auto max-w-[1100px] px-[26px] pb-[72px]">
          {isLoggedIn && promo && (
            <div className="pt-4">
              <PromoBanner promo={promo} />
            </div>
          )}

          {searchActive ? (
            /* Search results surface directly below the bar + pills. */
            <section className="pt-3">
              <div className="mb-3.5 flex items-end justify-between gap-3">
                <div>
                  <SectionHeading
                    label="Results"
                    title={`${resultCount} result${resultCount === 1 ? '' : 's'} for “${searchTerm.trim()}”`}
                    className="mb-0"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="shrink-0 rounded-full border-[0.5px] border-line bg-surface px-3 py-1.5 text-[12px] font-semibold text-text hover:border-brand hover:text-brand"
                >
                  Clear
                </button>
              </div>

              {resultCount === 0 ? (
                <div className="rounded-card border-[0.5px] border-line bg-surface px-5 py-12 text-center text-sm text-text-muted">
                  No channels, connectors, or commands match &ldquo;{searchTerm.trim()}&rdquo;.
                </div>
              ) : (
                <div className="space-y-7">
                  {matchedChannels.length > 0 && (
                    <div>
                      <SectionLabel className="mb-2">Channels</SectionLabel>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3.5">
                        {matchedChannels.map((c) => (
                          <ChannelGridCard
                            key={c.key}
                            channel={c}
                            onOpen={setSelectedChannelKey}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {connectorMatchCount > 0 && (
                    <div>
                      <SectionLabel className="mb-2">Connectors</SectionLabel>
                      <ConnectorsAccordion
                        channels={channels}
                        searchTerm={searchTerm}
                        initialOpenChannelKey={initialChannelKey}
                        isLoggedIn={isLoggedIn}
                        onOpen={handleOpenSubchannel}
                      />
                    </div>
                  )}
                  {filteredCommands.length > 0 && (
                    <div>
                      <SectionLabel className="mb-2">Commands</SectionLabel>
                      <div className="flex flex-wrap gap-3.5">
                        {filteredCommands.map((c) => (
                          <CommandCard key={c.id} command={c} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          ) : (
            <>
              {/* Sections render in `orderedSectionIds`: the axis pill selection
                  sorts its matching section to the top, directly below the pills. */}
              {orderedSectionIds.map((id) => (
                <div key={id}>{sectionNodes[id]}</div>
              ))}
            </>
          )}
        </div>
      </div>
      <ChannelOverlay
        channel={selectedChannel}
        open={selectedChannel !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedChannelKey(null)
        }}
        onOpenSubchannel={handleOpenSubchannel}
      />
      <SettingsConnectionPanel
        subchannel={openSubchannel}
        channelAccentHex={enablingChannel?.accent_hex ?? '#0A8A6E'}
        channelName={enablingChannel?.name ?? ''}
        onClose={() => setOpenSubchannel(null)}
        onChanged={() => router.refresh()}
      />
    </GateProvider>
  )
}

/** Trailing card at the end of the Commands rail — Create automation CTA. */
function CreateAutomationCard() {
  const { requireAuth } = useGate()
  return (
    <button
      type="button"
      onClick={() => requireAuth()}
      className="flex min-h-[172px] flex-[0_0_258px] flex-col items-center justify-center gap-2 rounded-[16px] border-[1.5px] border-dashed border-line-2 bg-canvas p-4 text-center transition-transform duration-200 ease-out [scroll-snap-align:start] hover:-translate-y-[2px]"
    >
      <span className="grid size-10 place-items-center rounded-full bg-brand-tint text-brand">
        <HugeiconsIcon icon={PlusSignIcon} className="size-5" aria-hidden />
      </span>
      <div className="text-sm font-bold text-text-strong">
        Create automation
      </div>
      <div className="font-mono text-[11px] text-text-subtle">
        Build your own
      </div>
    </button>
  )
}
